import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { IOption, SearchInput, Select, Typography } from "@digital-services/ui";
import { usePaginationContext } from "@ds-cps/ui";

import {
  firstSearchSelectOption,
  SearchOrganizationsByEnum,
  searchOrganizationsBySelectOptions
} from "@organization/models/Organization";

import { useSearchOrganizationsContext } from "./context/useSearchOrganizationsContext";

interface Props {
  extendSearch?: boolean;
}

export const SearchOrganizations = (props: Props) => {
  const { extendSearch } = props;

  const { t } = useTranslation(undefined, {
    keyPrefix: "organizations"
  });

  const { pageIndex, setPageIndex } = usePaginationContext();

  const { searchQuery, setSearchQuery, searchBy, setSearchBy } =
    useSearchOrganizationsContext();

  const [selectedOption, setSelectedOption] = useState<IOption | null>(
    firstSearchSelectOption
  );

  const handleSearchInput = useCallback(
    (newValue: string) => {
      // IMPORTANT
      // When this function enters with no actual change it cause `setSearchQuery()` to be executed in parent <SearchOrganizationsContextProvider>.
      // Which triggers then also re-render child <PaginationContextProvider>
      // Which then causes two contexts creation and <PageControls> clicks get broken.
      if (!newValue && !searchQuery) {
        return;
      }

      if (newValue === searchQuery) {
        // we need this to avoid below line from execution
        return;
      }

      if (newValue !== searchQuery) {
        setSearchQuery(newValue);
      }

      // This is to reset pagination when search happens
      // But when search value is in field, it BREAKs next pages pagination flow
      if (pageIndex >= 0) {
        setPageIndex(0);
      }
    },

    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchQuery, pageIndex]
  );

  const placeholderValue =
    searchBy === SearchOrganizationsByEnum.ByName
      ? t("search.placeholder-for-name")
      : t("search.placeholder-for-external-id");

  return (
    <>
      {extendSearch && (
        <>
          <Typography className="min-w-[200px] text-right">
            {t("search.prefix")}
          </Typography>
          <div className="w-[130px]">
            <Select
              data-testid="search-organizations-by-select"
              options={searchOrganizationsBySelectOptions}
              selected={selectedOption}
              onChange={newItem => {
                setSelectedOption(newItem);
                setSearchBy(newItem?.value as SearchOrganizationsByEnum);
                if (searchQuery) {
                  // Setting local variable `searchValue` as empty value is NOT enough to "reset" <SearchInput> state.
                  setSearchQuery("");
                  // For `<SearchInput>` initialValue doesn't actually work at all
                  // When we ONLY have this above line it doesn't cause <SearchInput> to change inside, because `initialValue` param was designed to indicate that it is only set once during initialization.
                  // Workaround here is - upon <Select> change to use `key` param with value based on <Select> selection value.
                  // Which at the end re-creates <SearchInput>
                }
              }}
            />
          </div>
        </>
      )}
      <div className="w-[500px]">
        <SearchInput
          key={`${selectedOption?.value}`}
          data-testid="search-organization"
          placeholder={placeholderValue}
          initialValue={searchQuery}
          onChange={handleSearchInput}
        />
      </div>
    </>
  );
};
